import { z } from 'zod';
import type { Food, NutrientKey, NutrientSources, NutrientValues } from '@/engine/types';
import type { NutrientUnit } from '@/engine/nutrients';
import { nutrientMeta } from '@/engine/nutrients';

/**
 * Traduce una respuesta de la API v3 de Open Food Facts a nuestro modelo.
 *
 * Todo el conocimiento del formato de OFF vive AQUÍ y solo aquí. v3 sigue
 * marcada como "under development" en su propia referencia OpenAPI, así que
 * cuando cambie solo hay que tocar este archivo (DECISIONS.md D-08).
 *
 * Contrato verificado contra la API real el 2026-09-20:
 *   { code, status: 'success'|'failure', result: {id}, product?, errors[], warnings[] }
 * OJO: la documentación habla de `status_id`, pero la API devuelve `status`.
 */

const nutrimentsSchema = z.record(z.string(), z.union([z.number(), z.string()])).default({});

// `.nullish()` y no `.optional()`: OFF devuelve `null` explícito en los campos
// que no ha podido rellenar (serving_size, serving_quantity...), y un esquema
// que solo acepta "ausente" tira la respuesta entera por un campo vacío.
const productSchema = z.object({
  code: z.string().nullish(),
  product_name: z.string().nullish(),
  product_name_es: z.string().nullish(),
  brands: z.string().nullish(),
  quantity: z.string().nullish(),
  product_quantity: z.union([z.number(), z.string()]).nullish(),
  product_quantity_unit: z.string().nullish(),
  serving_size: z.string().nullish(),
  serving_quantity: z.union([z.number(), z.string()]).nullish(),
  nutriments: nutrimentsSchema,
});

export const offResponseSchema = z.object({
  code: z.string().nullish(),
  status: z.string().nullish(),
  result: z.object({ id: z.string() }).nullish(),
  product: productSchema.nullish(),
  errors: z.array(z.unknown()).nullish(),
  warnings: z.array(z.unknown()).nullish(),
});

export type OffResponse = z.infer<typeof offResponseSchema>;

/** Clave de OFF -> clave nuestra. Solo lo que sabemos interpretar. */
const NUTRIENT_MAP: Record<string, NutrientKey> = {
  'energy-kcal': 'kcal',
  proteins: 'protein',
  fat: 'fat',
  'saturated-fat': 'saturatedFat',
  carbohydrates: 'carbs',
  sugars: 'sugars',
  fiber: 'fiber',
  sodium: 'sodium',
  calcium: 'calcium',
  iron: 'iron',
  magnesium: 'magnesium',
  phosphorus: 'phosphorus',
  potassium: 'potassium',
  zinc: 'zinc',
  copper: 'copper',
  selenium: 'selenium',
  iodine: 'iodine',
  manganese: 'manganese',
  'vitamin-a': 'vitaminA',
  'vitamin-c': 'vitaminC',
  'vitamin-d': 'vitaminD',
  'vitamin-e': 'vitaminE',
  'vitamin-k': 'vitaminK',
  'vitamin-b1': 'thiamin',
  'vitamin-b2': 'riboflavin',
  'vitamin-pp': 'niacin',
  'vitamin-b6': 'vitaminB6',
  'vitamin-b9': 'folate',
  'vitamin-b12': 'vitaminB12',
};

/** Factores a gramos. Sirve de puente entre cualquier par de unidades de masa. */
const TO_GRAMS: Record<string, number> = {
  g: 1,
  mg: 1e-3,
  µg: 1e-6,
  ug: 1e-6,
  mcg: 1e-6,
  kg: 1000,
};

export function convertMass(value: number, from: string, to: NutrientUnit): number | null {
  const fromFactor = TO_GRAMS[from.toLowerCase()];
  const toFactor = TO_GRAMS[to === 'ug' ? 'ug' : to];
  if (fromFactor == null || toFactor == null) return null;
  return (value * fromFactor) / toFactor;
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Extrae los nutrientes por 100 g.
 *
 * La unidad se lee del campo `<clave>_unit` de la PROPIA respuesta en vez de
 * asumirla: el sodio de OFF viene en gramos y nosotros lo guardamos en mg, así
 * que dar por hecha la unidad multiplicaría el sodio por mil.
 */
export function parseNutriments(nutriments: Record<string, number | string>): {
  values: NutrientValues;
  sources: NutrientSources;
} {
  const values: NutrientValues = {};
  const sources: NutrientSources = {};

  for (const [offKey, ourKey] of Object.entries(NUTRIENT_MAP)) {
    const raw = asNumber(nutriments[`${offKey}_100g`]);
    if (raw == null || raw < 0) continue;

    if (ourKey === 'kcal') {
      values.kcal = raw;
      sources.kcal = 'off';
      continue;
    }

    // Sin unidad declarada se asume gramos, que es como OFF normaliza los
    // valores `_100g`. Si viniera otra cosa, el campo `_unit` lo dice.
    const unit = String(nutriments[`${offKey}_unit`] ?? 'g');
    const converted = convertMass(raw, unit, nutrientMeta(ourKey).unit);
    if (converted == null) continue;

    values[ourKey] = round(converted);
    sources[ourKey] = 'off';
  }

  // EPA y DHA solo cuentan si están los dos: con uno solo el total sería falso
  // por defecto. Misma regla que en el seed de USDA.
  const epa = asNumber(nutriments['eicosapentaenoic-acid_100g']);
  const dha = asNumber(nutriments['docosahexaenoic-acid_100g']);
  if (epa != null && dha != null) {
    const unit = String(nutriments['eicosapentaenoic-acid_unit'] ?? 'g');
    const total = convertMass(epa + dha, unit, 'mg');
    if (total != null) {
      values.epaDha = round(total);
      sources.epaDha = 'off';
    }
  }

  return { values, sources };
}

function round(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}

export type OffLookup =
  { found: true; food: Food } | { found: false; reason: 'no_encontrado' | 'sin_datos' };

/**
 * Convierte la respuesta en un alimento listo para registrar, o dice por qué no.
 */
export function parseOffProduct(raw: unknown, barcode: string): OffLookup {
  const parsed = offResponseSchema.safeParse(raw);
  if (!parsed.success) return { found: false, reason: 'no_encontrado' };

  const { status, result, product } = parsed.data;
  const ok = status === 'success' || result?.id === 'product_found';
  if (!ok || !product) return { found: false, reason: 'no_encontrado' };

  const { values, sources } = parseNutriments(product.nutriments);
  // Sin calorías no se puede registrar nada útil.
  if (values.kcal == null) return { found: false, reason: 'sin_datos' };

  const name = (product.product_name_es ?? product.product_name ?? '').trim();
  const brand = product.brands?.split(',')[0]?.trim() ?? null;

  const packageGrams = packageWeight(product);
  const servingGrams = asNumber(product.serving_quantity);

  const food: Food = {
    id: `off-${barcode}`,
    kind: 'producto',
    name: name || `Producto ${barcode}`,
    synonyms: [],
    brand,
    barcode,
    per100g: values,
    sources,
    packageGrams,
    servingGrams: servingGrams != null && servingGrams > 0 ? servingGrams : null,
    householdPortions: [],
    tags: [],
  };

  return { found: true, food };
}

/**
 * Peso total del envase, necesario para los botones Entero/Medio/Cuarto.
 *
 * `product_quantity` es la versión numérica que calcula OFF a partir del texto
 * libre `quantity`. Solo vale si la unidad es de masa: un brik de 1 L declarado
 * en ml no da gramos sin conocer la densidad, y ahí no se adivina.
 */
export function packageWeight(product: {
  product_quantity?: number | string | null;
  product_quantity_unit?: string | null;
}): number | null {
  const value = asNumber(product.product_quantity);
  if (value == null || value <= 0) return null;

  const unit = (product.product_quantity_unit ?? 'g').toLowerCase();
  if (unit === 'g') return value;
  if (unit === 'kg') return value * 1000;
  // ml y l se dejan fuera a propósito: convertir volumen a masa exige densidad.
  return null;
}
